import { useState, useEffect } from "react";
import axios from "axios";
import { 
  PawPrint, Save, Trash2, Plus, ArrowLeft, Image, Video, 
  Heart, X, Check, Lock, LogOut, Key,
  Bone, Sun, Droplet
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "./ui/select";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter,
  DialogDescription
} from "./ui/dialog";
import { Toaster, toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon options for fun facts
const iconOptions = [
  { value: "heart", label: "Heart", icon: Heart },
  { value: "bone", label: "Bone", icon: Bone },
  { value: "sun", label: "Sun", icon: Sun },
  { value: "droplet", label: "Water Drop", icon: Droplet },
];

export default function AdminPanel({ onBack }) {
  // Auth states
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: "", new: "", confirm: "" });
  
  const [pet, setPet] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [funFacts, setFunFacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Dialog states
  const [photoDialog, setPhotoDialog] = useState(false);
  const [videoDialog, setVideoDialog] = useState(false);
  const [factDialog, setFactDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, id: null, title: "" });
  
  // Form states
  const [newPhoto, setNewPhoto] = useState({ url: "", caption: "", is_featured: false });
  const [newVideo, setNewVideo] = useState({ url: "", title: "", description: "", thumbnail_url: "" });
  const [newFact, setNewFact] = useState({ icon: "heart", title: "", description: "" });
  
  // Pet form state
  const [petForm, setPetForm] = useState({
    name: "",
    breed: "",
    age: "",
    bio: "",
    personality_traits: [],
    favorite_things: []
  });
  const [newTrait, setNewTrait] = useState("");
  const [newFavorite, setNewFavorite] = useState("");

  // Check authentication on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem("admin_token");
    if (token) {
      try {
        await axios.post(`${API}/admin/verify?token=${token}`);
        setIsAuthenticated(true);
        fetchAllData();
      } catch {
        localStorage.removeItem("admin_token");
        setIsAuthenticated(false);
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
    setAuthLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const res = await axios.post(`${API}/admin/login`, { password: loginPassword });
      localStorage.setItem("admin_token", res.data.token);
      setIsAuthenticated(true);
      setLoginPassword("");
      if (res.data.message) {
        toast.info(res.data.message);
      }
      fetchAllData();
    } catch (error) {
      setLoginError(error.response?.data?.detail || "Invalid password");
    }
  };

  const handleLogout = async () => {
    const token = localStorage.getItem("admin_token");
    try {
      await axios.post(`${API}/admin/logout?token=${token}`);
    } catch {}
    localStorage.removeItem("admin_token");
    setIsAuthenticated(false);
    onBack();
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.new !== passwordForm.confirm) {
      toast.error("New passwords don't match");
      return;
    }
    if (passwordForm.new.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    try {
      await axios.post(`${API}/admin/change-password`, {
        current_password: passwordForm.current,
        new_password: passwordForm.new
      });
      toast.success("Password changed! Please login again.");
      localStorage.removeItem("admin_token");
      setIsAuthenticated(false);
      setShowChangePassword(false);
      setPasswordForm({ current: "", new: "", confirm: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    }
  };

  // Fetch all data
  useEffect(() => {
    if (isAuthenticated) {
      fetchAllData();
    }
  }, [isAuthenticated]);

  const fetchAllData = async () => {
    try {
      const [petRes, photosRes, videosRes, factsRes] = await Promise.all([
        axios.get(`${API}/pet`),
        axios.get(`${API}/photos`),
        axios.get(`${API}/videos`),
        axios.get(`${API}/facts`),
      ]);
      
      setPet(petRes.data);
      if (petRes.data) {
        setPetForm({
          name: petRes.data.name || "",
          breed: petRes.data.breed || "",
          age: petRes.data.age || "",
          bio: petRes.data.bio || "",
          personality_traits: petRes.data.personality_traits || [],
          favorite_things: petRes.data.favorite_things || []
        });
      }
      setPhotos(photosRes.data || []);
      setVideos(videosRes.data || []);
      setFunFacts(factsRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Save pet profile
  const savePetProfile = async () => {
    setSaving(true);
    try {
      if (pet) {
        await axios.put(`${API}/pet`, petForm);
      } else {
        await axios.post(`${API}/pet`, petForm);
      }
      toast.success("Pet profile saved!");
      fetchAllData();
    } catch (error) {
      console.error("Error saving pet:", error);
      toast.error("Failed to save pet profile");
    } finally {
      setSaving(false);
    }
  };

  // Add trait
  const addTrait = () => {
    if (newTrait.trim()) {
      setPetForm(prev => ({
        ...prev,
        personality_traits: [...prev.personality_traits, newTrait.trim()]
      }));
      setNewTrait("");
    }
  };

  // Remove trait
  const removeTrait = (index) => {
    setPetForm(prev => ({
      ...prev,
      personality_traits: prev.personality_traits.filter((_, i) => i !== index)
    }));
  };

  // Add favorite
  const addFavorite = () => {
    if (newFavorite.trim()) {
      setPetForm(prev => ({
        ...prev,
        favorite_things: [...prev.favorite_things, newFavorite.trim()]
      }));
      setNewFavorite("");
    }
  };

  // Remove favorite
  const removeFavorite = (index) => {
    setPetForm(prev => ({
      ...prev,
      favorite_things: prev.favorite_things.filter((_, i) => i !== index)
    }));
  };

  // Add photo
  const addPhoto = async () => {
    if (!newPhoto.url) {
      toast.error("Please enter a photo URL");
      return;
    }
    try {
      await axios.post(`${API}/photos`, newPhoto);
      toast.success("Photo added!");
      setPhotoDialog(false);
      setNewPhoto({ url: "", caption: "", is_featured: false });
      fetchAllData();
    } catch (error) {
      toast.error("Failed to add photo");
    }
  };

  // Add video
  const addVideo = async () => {
    if (!newVideo.url || !newVideo.title) {
      toast.error("Please enter video URL and title");
      return;
    }
    try {
      // Convert YouTube/Vimeo URLs to embed format
      let embedUrl = newVideo.url;
      if (newVideo.url.includes("youtube.com/watch")) {
        const videoId = newVideo.url.split("v=")[1]?.split("&")[0];
        embedUrl = `https://www.youtube.com/embed/${videoId}`;
      } else if (newVideo.url.includes("youtu.be/")) {
        const videoId = newVideo.url.split("youtu.be/")[1]?.split("?")[0];
        embedUrl = `https://www.youtube.com/embed/${videoId}`;
      } else if (newVideo.url.includes("vimeo.com/")) {
        const videoId = newVideo.url.split("vimeo.com/")[1]?.split("?")[0];
        embedUrl = `https://player.vimeo.com/video/${videoId}`;
      }
      
      await axios.post(`${API}/videos`, { ...newVideo, url: embedUrl });
      toast.success("Video added!");
      setVideoDialog(false);
      setNewVideo({ url: "", title: "", description: "", thumbnail_url: "" });
      fetchAllData();
    } catch (error) {
      toast.error("Failed to add video");
    }
  };

  // Add fun fact
  const addFunFact = async () => {
    if (!newFact.title || !newFact.description) {
      toast.error("Please enter title and description");
      return;
    }
    try {
      await axios.post(`${API}/facts`, newFact);
      toast.success("Fun fact added!");
      setFactDialog(false);
      setNewFact({ icon: "heart", title: "", description: "" });
      fetchAllData();
    } catch (error) {
      toast.error("Failed to add fun fact");
    }
  };

  // Delete item
  const deleteItem = async () => {
    const { type, id } = deleteDialog;
    try {
      await axios.delete(`${API}/${type}/${id}`);
      toast.success("Deleted successfully!");
      setDeleteDialog({ open: false, type: null, id: null, title: "" });
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFCF8]" data-testid="admin-loading">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#D4A373] animate-pulse flex items-center justify-center">
            <PawPrint className="w-8 h-8 text-white" />
          </div>
          <p className="font-heading text-xl text-[#2C3E2C]">Loading...</p>
        </div>
      </div>
    );
  }

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFCF8]" data-testid="admin-login">
        <Toaster position="top-right" richColors />
        <div className="w-full max-w-md px-6">
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-[#E5E5E5]">
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#D4A373] flex items-center justify-center">
                <Lock className="w-8 h-8 text-white" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-[#2C3E2C]">Admin Login</h1>
              <p className="text-[#2C3E2C]/60 mt-2">Enter your password to access the admin panel</p>
            </div>
            
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  placeholder="Enter admin password"
                  className="border-[#E5E5E5] focus:border-[#D4A373]"
                  data-testid="admin-password-input"
                  required
                />
              </div>
              
              {loginError && (
                <p className="text-red-500 text-sm" data-testid="login-error">{loginError}</p>
              )}
              
              <Button 
                type="submit" 
                className="w-full bg-[#D4A373] hover:bg-[#C59262] text-white"
                data-testid="admin-login-btn"
              >
                <Lock className="w-4 h-4 mr-2" /> Login
              </Button>
            </form>
            
            <div className="mt-6 text-center">
              <button 
                onClick={onBack}
                className="text-[#2C3E2C]/60 hover:text-[#D4A373] text-sm"
              >
                ← Back to website
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFCF8]" data-testid="admin-loading">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#D4A373] animate-pulse flex items-center justify-center">
            <PawPrint className="w-8 h-8 text-white" />
          </div>
          <p className="font-heading text-xl text-[#2C3E2C]">Loading Admin Panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFCF8]" data-testid="admin-panel">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-[#E5E5E5]" data-testid="admin-header">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={onBack}
              className="p-2 hover:bg-[#FAEDCD] rounded-full transition-colors"
              data-testid="admin-back-btn"
            >
              <ArrowLeft className="w-5 h-5 text-[#2C3E2C]" />
            </button>
            <h1 className="font-heading text-2xl font-bold text-[#2C3E2C]">
              Admin Panel
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowChangePassword(true)}
              className="p-2 hover:bg-[#FAEDCD] rounded-full transition-colors"
              title="Change Password"
              data-testid="change-password-btn"
            >
              <Key className="w-5 h-5 text-[#2C3E2C]" />
            </button>
            <button
              onClick={handleLogout}
              className="p-2 hover:bg-red-50 rounded-full transition-colors"
              title="Logout"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5 text-red-500" />
            </button>
          </div>
        </div>
      </header>

      {/* Change Password Dialog */}
      <Dialog open={showChangePassword} onOpenChange={setShowChangePassword}>
        <DialogContent className="bg-white" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Change Password</DialogTitle>
            <DialogDescription>Enter your current password and choose a new one</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Current Password</Label>
              <Input
                type="password"
                value={passwordForm.current}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, current: e.target.value }))}
                placeholder="Enter current password"
                required
                data-testid="current-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input
                type="password"
                value={passwordForm.new}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, new: e.target.value }))}
                placeholder="Enter new password (min 6 characters)"
                required
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Confirm New Password</Label>
              <Input
                type="password"
                value={passwordForm.confirm}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm: e.target.value }))}
                placeholder="Confirm new password"
                required
                data-testid="confirm-password-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowChangePassword(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#D4A373] hover:bg-[#C59262] text-white" data-testid="save-password-btn">
                <Check className="w-4 h-4 mr-2" /> Save Password
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        <Tabs defaultValue="profile" className="space-y-8">
          <TabsList className="bg-[#FAEDCD] p-1 rounded-xl" data-testid="admin-tabs">
            <TabsTrigger value="profile" className="data-[state=active]:bg-white rounded-lg px-4 py-2" data-testid="tab-profile">
              <PawPrint className="w-4 h-4 mr-2" /> Profile
            </TabsTrigger>
            <TabsTrigger value="photos" className="data-[state=active]:bg-white rounded-lg px-4 py-2" data-testid="tab-photos">
              <Image className="w-4 h-4 mr-2" /> Photos
            </TabsTrigger>
            <TabsTrigger value="videos" className="data-[state=active]:bg-white rounded-lg px-4 py-2" data-testid="tab-videos">
              <Video className="w-4 h-4 mr-2" /> Videos
            </TabsTrigger>
            <TabsTrigger value="facts" className="data-[state=active]:bg-white rounded-lg px-4 py-2" data-testid="tab-facts">
              <Heart className="w-4 h-4 mr-2" /> Fun Facts
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-6" data-testid="profile-content">
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-[#E5E5E5]">
              <h2 className="font-heading text-2xl font-bold text-[#2C3E2C] mb-6">Pet Profile</h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Pet Name</Label>
                  <Input 
                    id="name"
                    value={petForm.name}
                    onChange={(e) => setPetForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Buddy"
                    className="border-[#E5E5E5] focus:border-[#D4A373]"
                    data-testid="input-pet-name"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="breed">Breed</Label>
                  <Input 
                    id="breed"
                    value={petForm.breed}
                    onChange={(e) => setPetForm(prev => ({ ...prev, breed: e.target.value }))}
                    placeholder="e.g., Golden Retriever"
                    className="border-[#E5E5E5] focus:border-[#D4A373]"
                    data-testid="input-breed"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="age">Age</Label>
                  <Input 
                    id="age"
                    value={petForm.age}
                    onChange={(e) => setPetForm(prev => ({ ...prev, age: e.target.value }))}
                    placeholder="e.g., 3 years old"
                    className="border-[#E5E5E5] focus:border-[#D4A373]"
                    data-testid="input-age"
                  />
                </div>
              </div>
              
              <div className="space-y-2 mt-6">
                <Label htmlFor="bio">Bio / Description</Label>
                <Textarea 
                  id="bio"
                  value={petForm.bio}
                  onChange={(e) => setPetForm(prev => ({ ...prev, bio: e.target.value }))}
                  placeholder="Tell everyone about your pet..."
                  className="border-[#E5E5E5] focus:border-[#D4A373] min-h-[120px]"
                  data-testid="input-bio"
                />
              </div>

              {/* Personality Traits */}
              <div className="space-y-3 mt-6">
                <Label>Personality Traits</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {petForm.personality_traits.map((trait, index) => (
                    <span 
                      key={index} 
                      className="inline-flex items-center gap-1 px-3 py-1 bg-[#A3B18A]/30 rounded-full text-sm"
                      data-testid={`trait-badge-${index}`}
                    >
                      {trait}
                      <button onClick={() => removeTrait(index)} className="hover:text-[#E76F51]">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input 
                    value={newTrait}
                    onChange={(e) => setNewTrait(e.target.value)}
                    placeholder="Add a trait (e.g., Playful)"
                    className="border-[#E5E5E5] focus:border-[#D4A373]"
                    onKeyPress={(e) => e.key === 'Enter' && addTrait()}
                    data-testid="input-new-trait"
                  />
                  <Button onClick={addTrait} variant="outline" className="border-[#D4A373] text-[#D4A373] hover:bg-[#D4A373] hover:text-white" data-testid="btn-add-trait">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Favorite Things */}
              <div className="space-y-3 mt-6">
                <Label>Favorite Things</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {petForm.favorite_things.map((thing, index) => (
                    <span 
                      key={index} 
                      className="inline-flex items-center gap-1 px-3 py-1 bg-[#D4A373]/30 rounded-full text-sm"
                      data-testid={`favorite-badge-${index}`}
                    >
                      {thing}
                      <button onClick={() => removeFavorite(index)} className="hover:text-[#E76F51]">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input 
                    value={newFavorite}
                    onChange={(e) => setNewFavorite(e.target.value)}
                    placeholder="Add a favorite (e.g., Tennis balls)"
                    className="border-[#E5E5E5] focus:border-[#D4A373]"
                    onKeyPress={(e) => e.key === 'Enter' && addFavorite()}
                    data-testid="input-new-favorite"
                  />
                  <Button onClick={addFavorite} variant="outline" className="border-[#D4A373] text-[#D4A373] hover:bg-[#D4A373] hover:text-white" data-testid="btn-add-favorite">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="mt-8 flex justify-end">
                <Button 
                  onClick={savePetProfile}
                  disabled={saving}
                  className="bg-[#D4A373] hover:bg-[#C59262] text-white px-8"
                  data-testid="btn-save-profile"
                >
                  {saving ? "Saving..." : <><Save className="w-4 h-4 mr-2" /> Save Profile</>}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Photos Tab */}
          <TabsContent value="photos" className="space-y-6" data-testid="photos-content">
            <div className="flex justify-between items-center">
              <h2 className="font-heading text-2xl font-bold text-[#2C3E2C]">Photo Gallery</h2>
              <Button 
                onClick={() => setPhotoDialog(true)}
                className="bg-[#D4A373] hover:bg-[#C59262] text-white"
                data-testid="btn-add-photo"
              >
                <Plus className="w-4 h-4 mr-2" /> Add Photo
              </Button>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {photos.map((photo, index) => (
                <div key={photo.id} className="relative group rounded-xl overflow-hidden aspect-square bg-[#FAEDCD]" data-testid={`photo-card-${index}`}>
                  <img src={photo.url} alt={photo.caption} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    {photo.is_featured && (
                      <span className="absolute top-2 left-2 bg-[#E76F51] text-white text-xs px-2 py-1 rounded">Featured</span>
                    )}
                    <button 
                      onClick={() => setDeleteDialog({ open: true, type: "photos", id: photo.id, title: photo.caption || "this photo" })}
                      className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600"
                      data-testid={`delete-photo-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {photo.caption && (
                    <p className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-sm p-2 truncate">
                      {photo.caption}
                    </p>
                  )}
                </div>
              ))}
            </div>
            
            {photos.length === 0 && (
              <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-[#E5E5E5]">
                <Image className="w-12 h-12 mx-auto mb-4 text-[#D4A373]" />
                <p className="text-[#2C3E2C]/70">No photos yet. Add your first photo!</p>
              </div>
            )}
          </TabsContent>

          {/* Videos Tab */}
          <TabsContent value="videos" className="space-y-6" data-testid="videos-content">
            <div className="flex justify-between items-center">
              <h2 className="font-heading text-2xl font-bold text-[#2C3E2C]">Video Gallery</h2>
              <Button 
                onClick={() => setVideoDialog(true)}
                className="bg-[#D4A373] hover:bg-[#C59262] text-white"
                data-testid="btn-add-video"
              >
                <Plus className="w-4 h-4 mr-2" /> Add Video
              </Button>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {videos.map((video, index) => (
                <div key={video.id} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-[#E5E5E5]" data-testid={`video-card-${index}`}>
                  <div className="aspect-video">
                    <iframe 
                      src={video.url}
                      title={video.title}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                  <div className="p-4 flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold text-[#2C3E2C]">{video.title}</h3>
                      {video.description && <p className="text-sm text-[#2C3E2C]/70">{video.description}</p>}
                    </div>
                    <button 
                      onClick={() => setDeleteDialog({ open: true, type: "videos", id: video.id, title: video.title })}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-full"
                      data-testid={`delete-video-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            
            {videos.length === 0 && (
              <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-[#E5E5E5]">
                <Video className="w-12 h-12 mx-auto mb-4 text-[#D4A373]" />
                <p className="text-[#2C3E2C]/70">No videos yet. Add YouTube or Vimeo links!</p>
              </div>
            )}
          </TabsContent>

          {/* Fun Facts Tab */}
          <TabsContent value="facts" className="space-y-6" data-testid="facts-content">
            <div className="flex justify-between items-center">
              <h2 className="font-heading text-2xl font-bold text-[#2C3E2C]">Fun Facts</h2>
              <Button 
                onClick={() => setFactDialog(true)}
                className="bg-[#D4A373] hover:bg-[#C59262] text-white"
                data-testid="btn-add-fact"
              >
                <Plus className="w-4 h-4 mr-2" /> Add Fun Fact
              </Button>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {funFacts.map((fact, index) => {
                const IconComponent = iconOptions.find(i => i.value === fact.icon)?.icon || Heart;
                return (
                  <div key={fact.id} className="bg-[#FAEDCD]/50 rounded-2xl p-6 relative group" data-testid={`fact-card-${index}`}>
                    <button 
                      onClick={() => setDeleteDialog({ open: true, type: "facts", id: fact.id, title: fact.title })}
                      className="absolute top-4 right-4 p-1 text-red-500 opacity-0 group-hover:opacity-100 hover:bg-red-50 rounded-full transition-opacity"
                      data-testid={`delete-fact-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <div className="w-12 h-12 bg-[#D4A373] rounded-full flex items-center justify-center mb-4">
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-[#2C3E2C] mb-2">{fact.title}</h3>
                    <p className="text-[#2C3E2C]/70 text-sm">{fact.description}</p>
                  </div>
                );
              })}
            </div>
            
            {funFacts.length === 0 && (
              <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-[#E5E5E5]">
                <Heart className="w-12 h-12 mx-auto mb-4 text-[#D4A373]" />
                <p className="text-[#2C3E2C]/70">No fun facts yet. Share something special about your pet!</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Add Photo Dialog */}
      <Dialog open={photoDialog} onOpenChange={setPhotoDialog}>
        <DialogContent className="bg-white" data-testid="photo-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Add Photo</DialogTitle>
            <DialogDescription>Add a new photo to your gallery</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Photo URL</Label>
              <Input 
                value={newPhoto.url}
                onChange={(e) => setNewPhoto(prev => ({ ...prev, url: e.target.value }))}
                placeholder="https://example.com/photo.jpg"
                data-testid="input-photo-url"
              />
              <p className="text-xs text-[#2C3E2C]/50">Paste a direct link to an image (Unsplash, Imgur, etc.)</p>
            </div>
            <div className="space-y-2">
              <Label>Caption (optional)</Label>
              <Input 
                value={newPhoto.caption}
                onChange={(e) => setNewPhoto(prev => ({ ...prev, caption: e.target.value }))}
                placeholder="A fun day at the park"
                data-testid="input-photo-caption"
              />
            </div>
            <div className="flex items-center gap-2">
              <input 
                type="checkbox"
                id="featured"
                checked={newPhoto.is_featured}
                onChange={(e) => setNewPhoto(prev => ({ ...prev, is_featured: e.target.checked }))}
                className="rounded border-[#E5E5E5]"
                data-testid="checkbox-featured"
              />
              <Label htmlFor="featured" className="cursor-pointer">Set as featured photo (hero image)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPhotoDialog(false)}>Cancel</Button>
            <Button onClick={addPhoto} className="bg-[#D4A373] hover:bg-[#C59262] text-white" data-testid="btn-save-photo">
              <Check className="w-4 h-4 mr-2" /> Add Photo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Video Dialog */}
      <Dialog open={videoDialog} onOpenChange={setVideoDialog}>
        <DialogContent className="bg-white" data-testid="video-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Add Video</DialogTitle>
            <DialogDescription>Add a YouTube or Vimeo video</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Video URL</Label>
              <Input 
                value={newVideo.url}
                onChange={(e) => setNewVideo(prev => ({ ...prev, url: e.target.value }))}
                placeholder="https://www.youtube.com/watch?v=..."
                data-testid="input-video-url"
              />
              <p className="text-xs text-[#2C3E2C]/50">Supports YouTube and Vimeo links</p>
            </div>
            <div className="space-y-2">
              <Label>Title</Label>
              <Input 
                value={newVideo.title}
                onChange={(e) => setNewVideo(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Buddy's first swim"
                data-testid="input-video-title"
              />
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Textarea 
                value={newVideo.description}
                onChange={(e) => setNewVideo(prev => ({ ...prev, description: e.target.value }))}
                placeholder="A memorable moment..."
                data-testid="input-video-description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVideoDialog(false)}>Cancel</Button>
            <Button onClick={addVideo} className="bg-[#D4A373] hover:bg-[#C59262] text-white" data-testid="btn-save-video">
              <Check className="w-4 h-4 mr-2" /> Add Video
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Fun Fact Dialog */}
      <Dialog open={factDialog} onOpenChange={setFactDialog}>
        <DialogContent className="bg-white" data-testid="fact-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Add Fun Fact</DialogTitle>
            <DialogDescription>Share something fun about your pet</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Icon</Label>
              <Select value={newFact.icon} onValueChange={(value) => setNewFact(prev => ({ ...prev, icon: value }))}>
                <SelectTrigger data-testid="select-fact-icon">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {iconOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <div className="flex items-center gap-2">
                        <opt.icon className="w-4 h-4" /> {opt.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Title</Label>
              <Input 
                value={newFact.title}
                onChange={(e) => setNewFact(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Loves Everyone"
                data-testid="input-fact-title"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea 
                value={newFact.description}
                onChange={(e) => setNewFact(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Buddy has never met a stranger..."
                data-testid="input-fact-description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFactDialog(false)}>Cancel</Button>
            <Button onClick={addFunFact} className="bg-[#D4A373] hover:bg-[#C59262] text-white" data-testid="btn-save-fact">
              <Check className="w-4 h-4 mr-2" /> Add Fact
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="bg-white" data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-red-500">Confirm Delete</DialogTitle>
            <DialogDescription>This action cannot be undone</DialogDescription>
          </DialogHeader>
          <p className="py-4">Are you sure you want to delete "{deleteDialog.title}"?</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, type: null, id: null, title: "" })}>
              Cancel
            </Button>
            <Button onClick={deleteItem} className="bg-red-500 hover:bg-red-600 text-white" data-testid="btn-confirm-delete">
              <Trash2 className="w-4 h-4 mr-2" /> Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
